import { useEffect } from 'react';
import type { ReactElement } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import RequireSession from '@quoruum/frontend-common/components/RequireSession';
import { createOrgStorage } from '@quoruum/frontend-common/lib/storage';
import LoginPage from './pages/LoginPage';
import { HomePage } from './HomePage';
import { HotelExplorer } from './pages/hotelmappage/HotelExplorer';
import { DashboardPage } from './pages/DashboardPage';
import { TraceViewer } from './pages/TraceViewer';
import { ChangeLog } from './pages/ChangeLog';
import { EnqueueCenter } from './pages/EnqueueCenter';
import { Reports } from './pages/Reports';
import { Companies } from './pages/Companies';
import { SystemValues } from './pages/SystemValues';
import { BookingEngines } from './pages/BookingEngines';
import { DataRequest } from './pages/DataRequests';
import { EntityExplorer } from './pages/EntityExplorer';
import { startSentryTraceLinkifier } from './util/sentryTraceLinkifier';

const QUORUUM_ORG_ID = 'or1';
const quoruumOrgStorage = createOrgStorage(QUORUUM_ORG_ID);

function getPageTitle(pathname: string, search: string): string {
  const normalizedPath = pathname !== '/' ? pathname.replace(/\/+$/, '') : pathname;

  const routeTitles: Record<string, string> = {
    '/login': 'Login',
    '/': 'Dashboard',
    '/tools/hotel-explorer': 'Hotel Explorer',
    '/tools/trace-viewer': 'Trace Viewer',
    '/tools/changelog': 'Changelog Viewer',
    '/tools/enqueue-center': 'Enqueue Center',
    '/tools/reports': 'Reports',
    '/tools/companies': 'Companies',
    '/tools/system-values': 'System Values',
    '/tools/booking-engines': 'Booking Engines',
    '/tools/data-requests': 'Data Requests',
    '/tools/entity-explorer': 'Entity Explorer',
  };

  const routeIdParamKeys: Record<string, string[]> = {
    '/tools/hotel-explorer': ['selectedId', 'selectedCompanyId'],
    '/tools/trace-viewer': ['traceId'],
    '/tools/changelog': ['entityId'],
    '/tools/enqueue-center': ['hotelId'],
    '/tools/reports': ['hotelId'],
    '/tools/companies': ['companyId', 'companyCityId'],
    '/tools/booking-engines': ['bookingEngineType'],
  };

  const pageTitle = routeTitles[normalizedPath] ?? 'Admin App';
  const searchParams = new URLSearchParams(search);
  const idParamKeys = routeIdParamKeys[normalizedPath] ?? [];
  const firstMatchedId = idParamKeys
    .map((key) => searchParams.get(key)?.trim() ?? '')
    .find((value) => value.length > 0);
  const pageTitleWithId = firstMatchedId ? `${pageTitle} (${firstMatchedId})` : pageTitle;

  return `${pageTitleWithId} | Admin App`;
}

export default function App(): ReactElement {
  const location = useLocation();

  useEffect(() => {
    const stopLinkifier = startSentryTraceLinkifier(document.body);

    return stopLinkifier;
  }, []);

  useEffect(() => {
    document.title = getPageTitle(location.pathname, location.search);
  }, [location.pathname, location.search]);

  return (
    <Routes>
      <Route
        path="/login"
        element={<LoginPage ssoEnabled={import.meta.env.VITE_SSO_UI_ENABLED === 'true'} />}
      />
      <Route element={<RequireSession orgId={QUORUUM_ORG_ID} />}>
        <Route path="/" element={<HomePage />}>
          <Route index element={<DashboardPage />} />
          <Route
            path="tools/hotel-explorer"
            element={<HotelExplorer storage={quoruumOrgStorage} />}
          />
          <Route path="tools/trace-viewer" element={<TraceViewer />} />
          <Route path="tools/changelog" element={<ChangeLog />} />
          <Route path="tools/enqueue-center" element={<EnqueueCenter />} />
          <Route path="tools/reports" element={<Reports />} />
          <Route path="tools/companies" element={<Companies />} />
          <Route path="tools/system-values" element={<SystemValues />} />
          <Route path="tools/booking-engines" element={<BookingEngines />} />
          <Route path="tools/data-requests" element={<DataRequest />} />
          <Route path="tools/entity-explorer" element={<EntityExplorer />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
